import { useEffect, useState } from 'react';
import type { PhoneNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import {
  AsYouType,
  getCountryCallingCode,
  getExampleNumber
} from 'libphonenumber-js/max';
import type { CountryCallingCode, CountryCode } from 'libphonenumber-js/types';
import { formatTemplate } from '../utility/formatTemplate';

interface usePhoneMaskProps {
  countryCode: CountryCode;
  value: string;
  showPlaceholder: boolean;
  fieldLabelInside: boolean;
  placeholderChar: string;
}

interface BuildMaskFromExampleArgs {
  example: PhoneNumber;
  callingCode: string;
  placeholderChar: string;
}

const buildMaskFromExample = ({
  example,
  callingCode,
  placeholderChar
}: BuildMaskFromExampleArgs) => {
  const exampleFormattedInternational = example.formatInternational();
  const phoneCodeLength = callingCode.length;
  let countryCodeDigitsSeen = 0;

  return exampleFormattedInternational
    .split('')
    .map((ch) => {
      if (/\d/.test(ch)) {
        const shouldPreserveDigit = countryCodeDigitsSeen < phoneCodeLength;
        countryCodeDigitsSeen++;
        return shouldPreserveDigit ? ch : placeholderChar;
      }
      return ch;
    })
    .join('');
};

interface FormatAsYouTypeMaskArgs {
  value: string;
  countryCode: CountryCode;
  callingCode: CountryCallingCode;
}

const formatAsYouTypeMask = ({
  callingCode,
  countryCode,
  value
}: FormatAsYouTypeMaskArgs) => {
  const asYouTypeFormatter = new AsYouType(countryCode);
  asYouTypeFormatter.input(value);
  const formatterTemplate = asYouTypeFormatter.formatter.template;
  return formatterTemplate
    ? formatTemplate(formatterTemplate, callingCode)
    : '';
};

export function usePhoneMask({
  countryCode,
  value,
  showPlaceholder,
  fieldLabelInside,
  placeholderChar = '0'
}: usePhoneMaskProps) {
  const [mask, setMask] = useState('');

  useEffect(() => {
    if (!showPlaceholder || fieldLabelInside) {
      setMask('');
      return;
    }

    const callingCode = getCountryCallingCode(countryCode);
    const example = getExampleNumber(countryCode, examples);
    const phoneNumberWithoutPlus = value.startsWith('+')
      ? value.slice(1)
      : value;

    const isOnlyCountryCallingCodeEntered =
      phoneNumberWithoutPlus === callingCode;
    const isEnterNumberAfterCountryCallingCode =
      phoneNumberWithoutPlus.startsWith(callingCode) &&
      phoneNumberWithoutPlus.length > callingCode.length;

    if (example && (isOnlyCountryCallingCodeEntered || value === '')) {
      setMask(buildMaskFromExample({ example, callingCode, placeholderChar }));
      return;
    }

    if (isEnterNumberAfterCountryCallingCode) {
      setMask(formatAsYouTypeMask({ callingCode, countryCode, value }));
      return;
    }

    setMask('');
  }, [countryCode, value, showPlaceholder, placeholderChar, fieldLabelInside]);

  return mask;
}
